import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetPhdStudentsQuery,
  useGetPhdSchoolYearsQuery,
  useGetPhdSupervisorReferenceQuery,
  useGetPhdStudyFieldsQuery,
  useGetPhdStudySubjectsQuery,
} from '../../../../redux/services/phd';
import { useGetDepartmentsQuery } from '../../../../redux/services/departments';
import { useGetUsersQuery } from '../../../../redux/services/users';
import { usePhdSchoolYearOptions } from './utils/usePhdSchoolYearOptions';

const resolveFullName = (entity) => {
  if (!entity) {
    return undefined;
  }

  if (entity.full_name || entity.display_name || entity.name) {
    return entity.full_name || entity.display_name || entity.name;
  }

  const composedName = [
    entity.title_before,
    entity.first_name,
    entity.last_name,
    entity.title_after,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return composedName || undefined;
};

export const resolveStudentLabel = (student, personMap) => {
  if (!student) {
    return undefined;
  }

  return (
    student.student_display_name ||
    resolveFullName(student.person) ||
    personMap?.get(student.person_id) ||
    `${student.id}`
  );
};

const resolveDepartmentTitle = (department, language) => {
  if (language === 'en') {
    return department.title_en || department.title_sk || department.name;
  }

  return department.title_sk || department.title_en || department.name;
};

export const usePhdReferenceData = ({
  includeStudents = true,
  studentsRequireThesis = false,
  includeDepartments = true,
  includeStudyFields = true,
  includeStudySubjects = false,
  includeUsers = true,
  includeExaminers = false,
} = {}) => {
  const { i18n } = useTranslation();
  const studentsQuery = useGetPhdStudentsQuery(undefined, {
    skip: !includeStudents,
  });
  const schoolYearsQuery = useGetPhdSchoolYearsQuery();
  const supervisorsQuery = useGetPhdSupervisorReferenceQuery(undefined, {
    skip: !includeExaminers,
  });
  const departmentsQuery = useGetDepartmentsQuery(undefined, {
    skip: !includeDepartments,
  });
  const studyFieldsQuery = useGetPhdStudyFieldsQuery(undefined, {
    skip: !includeStudyFields,
  });
  const studySubjectsQuery = useGetPhdStudySubjectsQuery(undefined, {
    skip: !includeStudySubjects,
  });
  const usersQuery = useGetUsersQuery(
    { type: 'forAccountChooser' },
    { skip: !includeUsers },
  );

  const {
    schoolYearOptions,
    schoolYearMap,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    futureSchoolYears,
  } = usePhdSchoolYearOptions(schoolYearsQuery.data || []);

  const personMap = useMemo(() => {
    if (!includeUsers) {
      return new Map();
    }
    return new Map(
      (usersQuery.data || [])
        .filter(
          (person) =>
            !person.impersonate_type || person.impersonate_type === 'Person',
        )
        .map((person) => [
          person.person_id ?? person.impersonate_id,
          resolveFullName(person),
        ]),
    );
  }, [usersQuery.data, includeUsers]);

  const filteredStudents = useMemo(() => {
    if (!includeStudents) {
      return [];
    }
    if (!studentsRequireThesis) {
      return studentsQuery.data || [];
    }
    return (studentsQuery.data || []).filter((student) =>
      (student.theses || []).some(
        (thesis) => thesis.phd_student_id === student.id,
      ),
    );
  }, [studentsQuery.data, includeStudents, studentsRequireThesis]);

  const studentOptions = useMemo(() => {
    if (!includeStudents) {
      return [];
    }
    return filteredStudents.map((student) => ({
      value: student.id,
      label: resolveStudentLabel(student, personMap),
    }));
  }, [filteredStudents, personMap, includeStudents]);

  const studentDisplayNameMap = useMemo(() => {
    if (!includeStudents) {
      return new Map();
    }
    return new Map(
      filteredStudents.map((student) => [
        student.id,
        resolveStudentLabel(student, personMap),
      ]),
    );
  }, [filteredStudents, personMap, includeStudents]);

  const personOptions = useMemo(() => {
    if (!includeUsers) {
      return [];
    }
    return (usersQuery.data || [])
      .filter(
        (person) =>
          !person.impersonate_type || person.impersonate_type === 'Person',
      )
      .map((person) => ({
        value: person.person_id ?? person.impersonate_id,
        label:
          resolveFullName(person) ||
          person.mail ||
          `${person.person_id ?? person.impersonate_id}`,
      }));
  }, [usersQuery.data, includeUsers]);

  const departmentOptions = useMemo(() => {
    if (!includeDepartments) {
      return [];
    }
    return (departmentsQuery.data || []).map((department) => ({
      value: department.id,
      label: [
        department.code,
        resolveDepartmentTitle(department, i18n.language),
      ]
        .filter(Boolean)
        .join(' – '),
    }));
  }, [departmentsQuery.data, includeDepartments, i18n.language]);

  const departmentMap = useMemo(
    () =>
      new Map(
        (departmentsQuery.data || []).map((department) => [
          department.id,
          resolveDepartmentTitle(department, i18n.language),
        ]),
      ),
    [departmentsQuery.data, i18n.language],
  );

  const departmentCodeMap = useMemo(
    () =>
      new Map(
        (departmentsQuery.data || []).map((department) => [
          department.id,
          department.code,
        ]),
      ),
    [departmentsQuery.data],
  );

  const studyFieldOptions = useMemo(() => {
    if (!includeStudyFields) {
      return [];
    }
    return (studyFieldsQuery.data || []).map((field) => ({
      value: field.id,
      label: field.focus
        ? `${field.study_field} - ${field.focus}`
        : field.study_field,
    }));
  }, [studyFieldsQuery.data, includeStudyFields]);

  const studyFieldLabelOptions = useMemo(
    () =>
      studyFieldOptions.map((option) => ({ ...option, value: option.label })),
    [studyFieldOptions],
  );

  const studyFieldMap = useMemo(
    () =>
      new Map(
        (studyFieldsQuery.data || []).map((field) => [
          field.id,
          field.focus
            ? `${field.study_field} - ${field.focus}`
            : field.study_field,
        ]),
      ),
    [studyFieldsQuery.data],
  );

  const studyFieldCodeMap = useMemo(
    () =>
      new Map(
        (studyFieldsQuery.data || []).map((field) => [field.id, field.code]),
      ),
    [studyFieldsQuery.data],
  );

  const studySubjectOptions = useMemo(() => {
    if (!includeStudySubjects) {
      return [];
    }
    return (studySubjectsQuery.data || []).map((subject) => ({
      value: subject.id,
      label: subject.code ? `${subject.code} – ${subject.name}` : subject.name,
      name: subject.name,
      code: subject.code,
      completionType: subject.completion_type || 'S',
      subjectType: subject.subject_type || 'required',
    }));
  }, [studySubjectsQuery.data, includeStudySubjects]);

  const requiredStudySubjectOptions = useMemo(
    () =>
      studySubjectOptions.filter(
        (subject) => (subject.subjectType || 'required') === 'required',
      ),
    [studySubjectOptions],
  );

  const electiveStudySubjectOptions = useMemo(
    () =>
      studySubjectOptions.filter(
        (subject) => (subject.subjectType || 'required') === 'elective',
      ),
    [studySubjectOptions],
  );

  const examinerOptions = useMemo(() => {
    if (!includeExaminers) {
      return [];
    }
    return (supervisorsQuery.data || []).map((person) => ({
      value: person.person_id,
      label:
        person.full_name ||
        person.displayname ||
        person.mail ||
        `#${person.person_id}`,
    }));
  }, [supervisorsQuery.data, includeExaminers]);

  const examinerMap = useMemo(() => {
    if (!includeExaminers) {
      return new Map();
    }
    return new Map(
      (supervisorsQuery.data || []).map((person) => [
        person.person_id,
        person.full_name ||
          person.displayname ||
          person.mail ||
          `#${person.person_id}`,
      ]),
    );
  }, [supervisorsQuery.data, includeExaminers]);

  const isLoading =
    schoolYearsQuery.isLoading ||
    (includeStudents && studentsQuery.isLoading) ||
    (includeDepartments && departmentsQuery.isLoading) ||
    (includeStudyFields && studyFieldsQuery.isLoading) ||
    (includeStudySubjects && studySubjectsQuery.isLoading) ||
    (includeUsers && usersQuery.isLoading) ||
    (includeExaminers && supervisorsQuery.isLoading);

  return {
    personOptions,
    personMap,
    studentOptions,
    studentDisplayNameMap,
    schoolYearOptions,
    schoolYearMap,
    futureSchoolYears,
    departmentOptions,
    departmentMap,
    departmentCodeMap,
    studyFieldOptions,
    studyFieldLabelOptions,
    studyFieldMap,
    studyFieldCodeMap,
    studyFields: studyFieldsQuery.data || [],
    studySubjectOptions,
    requiredStudySubjectOptions,
    electiveStudySubjectOptions,
    studySubjects: studySubjectsQuery.data || [],
    examinerOptions,
    examinerMap,
    students: filteredStudents,
    schoolYears: schoolYearsQuery.data || [],
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading,
  };
};
